import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Send } from "lucide-react";
import { track } from "@/lib/analytics";
import {
  isIdentifierGuardError,
  roundsErrorMessage,
  useCreateRoundPost,
} from "@/lib/rounds";

export function ReplyComposer({
  citySlug,
  threadId,
  parentPostId = null,
  placeholder = "Add to the discussion…",
  onCancel,
  onPosted,
}: {
  citySlug: string;
  threadId: string;
  parentPostId?: string | null;
  placeholder?: string;
  onCancel?: () => void;
  onPosted?: () => void;
}) {
  const createPost = useCreateRoundPost(citySlug, threadId);
  const [body, setBody] = useState("");
  const identifierError = isIdentifierGuardError(createPost.error);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!body.trim()) return;
    createPost.mutate(
      { body: body.trim(), parentPostId },
      {
        onSuccess: (post) => {
          setBody("");
          track("post_create", { thread_id: threadId, post_id: post.id });
          onPosted?.();
        },
      },
    );
  };

  return (
    <form className="pb-rounds-composer" onSubmit={submit}>
      <label htmlFor={`rounds-reply-${parentPostId ?? "root"}`}>Your reply</label>
      <textarea
        id={`rounds-reply-${parentPostId ?? "root"}`}
        maxLength={4000}
        required
        value={body}
        placeholder={placeholder}
        aria-describedby={identifierError ? `rounds-reply-error-${parentPostId ?? "root"}` : undefined}
        onChange={(event) => {
          setBody(event.target.value);
          if (createPost.isError) createPost.reset();
        }}
      />
      {identifierError ? (
        <p className="pb-rounds-identifier-error" id={`rounds-reply-error-${parentPostId ?? "root"}`} role="alert">
          Remove patient-identifying details before posting. <Link to="/board/rounds/guidelines">Read the case-writing guidelines.</Link>
        </p>
      ) : createPost.isError ? (
        <p className="pb-rounds-form-error" role="alert">{roundsErrorMessage(createPost.error)}</p>
      ) : null}
      <footer>
        <span>{body.length.toLocaleString("en-AU")} / 4,000</span>
        <div>
          {onCancel ? <button type="button" className="pb-rounds-plain-button" onClick={onCancel}>Cancel</button> : null}
          <button className="pb-rounds-primary" type="submit" disabled={createPost.isPending || !body.trim()}>
            <Send size={13} aria-hidden="true" />
            {createPost.isPending ? "Posting…" : "Post reply"}
          </button>
        </div>
      </footer>
    </form>
  );
}
